// Maps the backend-serialized Pro plans (PersonalPlansProps concern →
// Subscription::Cancel::PlanSerializer) onto the two-term pricing surfaces
// (homepage Pricing, /membership TOF). One source of truth: change the
// Subscription::Plan records and every section follows. Monthly is ignored
// next to quarterly (killed 2026-07-10). With the flag membership-monthly
// (Foti, 2026-10-26) the server sends monthly + yearly: $75 a month or $675
// a year, and monthly comes first. The fallback keeps the sections rendering
// if the props are ever missing, with the prices of /membership.

export type PersonalPlanProp = {
  id: number
  billingInterval: 'monthly' | 'quarterly' | 'annually' | 'unknown'
  amount: number
  amountPerMonth: { subtotal: number; total: number; tax: number }
}

export type PricingTerm = {
  id: 'monthly' | 'quarterly' | 'annual'
  /** The Subscription::Plan record id — the paywall needs it to open checkout.
   *  0 when the fallback is in use (no plans prop), which the paywall treats as
   *  "not purchasable" rather than posting a bogus id. */
  planId: number
  termLabel: string
  perMonth: number
  total: number
  cycle: string
  billedLabel: string
  savePct: number
}

const FALLBACK: PricingTerm[] = [
  {
    id: 'monthly',
    planId: 0,
    termLabel: 'Monthly',
    perMonth: 75,
    total: 75,
    cycle: 'One call at a time',
    billedLabel: 'a month',
    savePct: 0,
  },
  {
    id: 'annual',
    planId: 0,
    termLabel: 'Yearly',
    perMonth: 56.25,
    total: 675,
    cycle: '3x, three calls at a time',
    billedLabel: 'a year',
    savePct: 25,
  },
]

export function pricingTerms(plans?: PersonalPlanProp[]): PricingTerm[] {
  const quarterly = plans?.find((p) => p.billingInterval === 'quarterly')
  const annual = plans?.find((p) => p.billingInterval === 'annually')
  const monthly = plans?.find((p) => p.billingInterval === 'monthly')
  // Monthly + Fast track (flag membership-monthly): monthly first, yearly saves.
  if (annual && monthly && !quarterly) {
    const mMo = monthly.amountPerMonth.total
    const aMo = annual.amountPerMonth.total
    return [
      {
        id: 'monthly',
        planId: monthly.id,
        termLabel: 'Monthly',
        perMonth: mMo,
        total: monthly.amount,
        cycle: 'One call at a time',
        billedLabel: 'a month',
        savePct: 0,
      },
      {
        id: 'annual',
        planId: annual.id,
        termLabel: 'Yearly',
        perMonth: aMo,
        total: annual.amount,
        cycle: '3x, three calls at a time',
        billedLabel: 'a year',
        savePct: mMo > 0 ? Math.round((1 - aMo / mMo) * 100) : 0,
      },
    ]
  }
  // One plan (flag membership-annual): the server sends the yearly plan only.
  if (annual && !quarterly)
    return [
      {
        id: 'annual',
        planId: annual.id,
        termLabel: '12 months',
        perMonth: annual.amountPerMonth.total,
        total: annual.amount,
        cycle: 'Billed once a year',
        billedLabel: 'billed annually',
        savePct: 0,
      },
    ]
  if (!quarterly || !annual) return FALLBACK

  const qMo = quarterly.amountPerMonth.total
  const aMo = annual.amountPerMonth.total
  return [
    {
      id: 'quarterly',
      planId: quarterly.id,
      termLabel: '3 months',
      perMonth: qMo,
      total: quarterly.amount,
      cycle: 'Billed every 3 months',
      billedLabel: 'billed quarterly',
      savePct: 0,
    },
    {
      id: 'annual',
      planId: annual.id,
      termLabel: '12 months',
      perMonth: aMo,
      total: annual.amount,
      cycle: 'Billed once a year',
      billedLabel: 'billed annually',
      savePct: qMo > 0 ? Math.round((1 - aMo / qMo) * 100) : 0,
    },
  ]
}
